import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, Activity, Target, Zap, DollarSign,
  BarChart3, PieChart, ArrowRight, CheckCircle, XCircle,
  RefreshCw, Clock, Users, Send, Eye, MessageCircle, MousePointer, Trash2
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

const JourneyDashboard = ({ user, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [channelPerformance, setChannelPerformance] = useState([]);
  const [engagementMetrics, setEngagementMetrics] = useState([]);
  const [engagementFunnel, setEngagementFunnel] = useState(null);
  const [engagementTiming, setEngagementTiming] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [timeRange, setTimeRange] = useState('last_7_days');
  const [error, setError] = useState(null);

  const hostTenantId = user?.host_tenant_id || 'default';

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getCurrentToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load all data in parallel
      const [overviewRes, channelRes, engagementRes, funnelRes, engagementFunnelRes, timingRes] = await Promise.all([
        apiCall(`/api/journey/overview/${hostTenantId}?time_range=${timeRange}`, { headers }),
        apiCall(`/api/journey/channel-performance/${hostTenantId}?time_range=${timeRange}`, { headers }),
        apiCall(`/api/journey/engagement-metrics/${hostTenantId}?time_range=${timeRange}`, { headers }),
        apiCall(`/api/journey/funnel-analysis/${hostTenantId}?time_range=${timeRange}`, { headers }),
        apiCall(`/api/journey/engagement-funnel/${hostTenantId}?time_range=${timeRange}`, { headers }),
        apiCall(`/api/journey/engagement-timing/${hostTenantId}?time_range=${timeRange}`, { headers })
      ]);

      if (overviewRes.ok && channelRes.ok && engagementRes.ok && funnelRes.ok && engagementFunnelRes.ok && timingRes.ok) {
        setOverview(await overviewRes.json());
        setChannelPerformance(await channelRes.json());
        setEngagementMetrics(await engagementRes.json());
        setFunnelData(await funnelRes.json());
        setEngagementFunnel(await engagementFunnelRes.json());
        setEngagementTiming(await timingRes.json());
      } else {
        throw new Error('Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error loading journey dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, color, format = 'number', subtitle }) => {
    const formattedValue = format === 'currency' 
      ? `$${value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
      : format === 'percentage'
      ? `${value?.toFixed(2) || 0}%`
      : format === 'time'
      ? `${value?.toFixed(1) || 0}s`
      : value?.toLocaleString() || '0';

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className={`text-2xl font-bold mt-2 ${color || 'text-gray-900'}`}>
              {formattedValue}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
            {change !== undefined && (
              <div className="flex items-center mt-2">
                {change >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(change).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${color ? color.replace('text', 'bg').replace('600', '100') : 'bg-gray-100'}`}>
            <Icon className={`h-6 w-6 ${color || 'text-gray-600'}`} />
          </div>
        </div>
      </div>
    );
  };

  const EngagementCard = ({ metrics }) => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MessageCircle className="h-5 w-5 mr-2" />
          {metrics.channel} - Engagement Details
        </h3>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Delivered</p>
            <p className="text-xl font-bold text-gray-900">{metrics.delivered_count.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Read Rate</p>
            <p className="text-xl font-bold text-blue-600">{metrics.read_rate.toFixed(1)}%</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Eye className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-gray-600">Read</span>
            </div>
            <span className="font-semibold">{metrics.read_count.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-gray-600">Seen</span>
            </div>
            <span className="font-semibold">{metrics.seen_count.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-4 w-4 text-purple-500" />
              <span className="text-sm text-gray-600">Replied</span>
            </div>
            <span className="font-semibold">{metrics.replied_count.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MousePointer className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-gray-600">Clicked</span>
            </div>
            <span className="font-semibold">{metrics.clicked_count.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Trash2 className="h-4 w-4 text-red-500" />
              <span className="text-sm text-gray-600">Deleted</span>
            </div>
            <span className="font-semibold">{metrics.deleted_count.toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-gray-500 mb-2">Average Time to Engagement</p>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">To Read:</span>
              <span className="font-medium">{(metrics.avg_seconds_to_read / 60).toFixed(1)} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">To Seen:</span>
              <span className="font-medium">{(metrics.avg_seconds_to_seen / 60).toFixed(1)} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">To Reply:</span>
              <span className="font-medium">{(metrics.avg_seconds_to_reply / 60).toFixed(1)} min</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EngagementFunnelViz = ({ data }) => {
    if (!data) return null;

    const stages = [
      { name: 'Sent', value: data.sent, color: 'bg-gray-500' },
      { name: 'Delivered', value: data.delivered, color: 'bg-blue-500', rate: data.delivery_rate },
      { name: 'Read', value: data.read, color: 'bg-green-500', rate: data.read_rate },
      { name: 'Seen', value: data.seen, color: 'bg-yellow-500', rate: data.seen_rate },
      { name: 'Replied', value: data.replied, color: 'bg-purple-500', rate: data.reply_rate },
    ];

    const maxValue = data.sent || 1;

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Funnel</h3>
        <div className="space-y-4">
          {stages.map((stage, index) => {
            const width = (stage.value / maxValue) * 100;
            return (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold">{stage.value.toLocaleString()}</span>
                    {stage.rate !== undefined && (
                      <span className="text-xs text-gray-500">({stage.rate.toFixed(1)}%)</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded h-10 flex items-center">
                  <div
                    className={`${stage.color} rounded h-10 flex items-center px-4 transition-all`}
                    style={{ width: `${width}%` }}
                  >
                    <span className="text-white font-semibold text-sm">
                      {width.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500">Overall Conversion</p>
            <p className="text-lg font-bold text-purple-600">
              {((data.replied / data.sent) * 100).toFixed(2)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Click Rate</p>
            <p className="text-lg font-bold text-orange-600">
              {((data.clicked / data.delivered) * 100).toFixed(2)}%
            </p>
          </div>
        </div>
      </div>
    );
  };

  const TimingAnalysis = ({ timings }) => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Timing Analysis</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Median</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">95th %ile</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {timings.map((timing, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {timing.engagement_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {timing.count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {timing.avg_minutes.toFixed(1)} min
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {timing.median_minutes.toFixed(1)} min
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(timing.p95_seconds / 60).toFixed(1)} min
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading dashboard: {error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Journey Analytics & Engagement"
        icon={TrendingUp}
        actions={
          <>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_90_days">Last 90 Days</option>
            </select>
            <button
              onClick={loadDashboardData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </>
        }
      />
      <PageBody padded={false} className="p-6">

      <div className="p-6 space-y-6">
        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Journeys"
            value={overview?.total_journeys}
            icon={Activity}
            color="text-blue-600"
          />
          <MetricCard
            title="Total Messages"
            value={overview?.total_messages}
            icon={Send}
            color="text-purple-600"
          />
          <MetricCard
            title="First Attempt Success"
            value={overview?.delivered_first_attempt}
            icon={CheckCircle}
            color="text-green-600"
          />
          <MetricCard
            title="After Retry Success"
            value={overview?.delivered_after_retry}
            icon={RefreshCw}
            color="text-orange-600"
          />
        </div>

        {/* Engagement Funnel */}
        {engagementFunnel && (
          <EngagementFunnelViz data={engagementFunnel} />
        )}

        {/* ROI and Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="ROI"
            value={overview?.roi}
            icon={DollarSign}
            color="text-green-600"
            format="percentage"
          />
          <MetricCard
            title="Channel Fallback Rate"
            value={overview?.channel_fallback_rate}
            icon={ArrowRight}
            color="text-yellow-600"
            format="percentage"
          />
          <MetricCard
            title="Avg Attempts to Delivery"
            value={overview?.avg_attempts_to_delivery}
            icon={Target}
            color="text-indigo-600"
          />
        </div>

        {/* Channel Engagement Details */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Channel Engagement Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {engagementMetrics.map((metrics, index) => (
              <EngagementCard key={index} metrics={metrics} />
            ))}
          </div>
        </div>

        {/* Engagement Timing Analysis */}
        {engagementTiming && engagementTiming.length > 0 && (
          <TimingAnalysis timings={engagementTiming} />
        )}

        {/* Quick Actions */}
        <div className="flex space-x-4">
          <button
            onClick={() => onNavigate && onNavigate('campaigns')}
            className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 transition-colors"
          >
            <Target className="h-5 w-5" />
            <span className="font-medium">View Campaigns</span>
          </button>
          <button
            onClick={() => onNavigate && onNavigate('hierarchy', { preset: 'journey' })}
            className="flex-1 px-6 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center space-x-2 transition-colors"
          >
            <BarChart3 className="h-5 w-5" />
            <span className="font-medium">Detailed Analysis</span>
          </button>
        </div>
      </div>
    </PageBody>
    </PageLayout>
  );
};

export default JourneyDashboard;
